import { getGoogleMapsApiKey } from '../config/maps';

export interface GoogleMapInstance {
  setCenter(position: { lat: number; lng: number }): void;
  panTo(position: { lat: number; lng: number }): void;
  setZoom(zoom: number): void;
  getZoom(): number | undefined;
  fitBounds(bounds: GoogleBounds, padding?: number): void;
  setMapTypeId(mapTypeId: string): void;
  setOptions(options: { styles?: unknown[] }): void;
}

export interface GoogleMarker {
  setMap(map: GoogleMapInstance | null): void;
  addListener(event: string, handler: () => void): void;
}

export interface GoogleBounds {
  extend(position: { lat: number; lng: number }): void;
  isEmpty(): boolean;
}

export interface GoogleMapsApi {
  Map: new (element: HTMLElement, options: Record<string, unknown>) => GoogleMapInstance;
  Marker: new (options: Record<string, unknown>) => GoogleMarker;
  LatLngBounds: new () => GoogleBounds;
  Size: new (width: number, height: number) => object;
  Point: new (x: number, y: number) => object;
}

type MapsWindow = Window & {
  google?: { maps?: GoogleMapsApi };
  gm_authFailure?: () => void;
  __mocklocInitGoogleMaps?: () => void;
};

const CALLBACK = '__mocklocInitGoogleMaps';
let loading: Promise<GoogleMapsApi | null> | null = null;

export function loadGoogleMaps(): Promise<GoogleMapsApi | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  const host = window as MapsWindow;
  if (host.google?.maps?.Map) return Promise.resolve(host.google.maps);
  if (loading) return loading;

  loading = new Promise((resolve) => {
    let settled = false;
    const finish = (value: GoogleMapsApi | null) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    const previousAuthFailure = host.gm_authFailure;
    host.gm_authFailure = () => {
      previousAuthFailure?.();
      finish(null);
    };

    host[CALLBACK] = () => {
      const maps = host.google?.maps;
      finish(maps?.Map ? maps : null);
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(getGoogleMapsApiKey())}&v=weekly&callback=${CALLBACK}`;
    script.onerror = () => finish(null);
    document.head.appendChild(script);
    window.setTimeout(() => finish(null), 12000);
  });

  return loading;
}
